import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const perfectFor = [
  { icon: 'ri-map-pin-2-line', text: 'You operate in a competitive local market where customers research before buying' },
  { icon: 'ri-handshake-line', text: 'You\'re in a service industry where trust and reputation drive decisions — trades, health, legal, hospitality, professional services' },
  { icon: 'ri-user-voice-line', text: 'Your business relies on word-of-mouth and referrals — and you want AI to become your most powerful referrer' },
  { icon: 'ri-building-4-line', text: 'You already have a solid website and want to extend your reach into AI-driven discovery' },
  { icon: 'ri-flag-line', text: 'You want to get ahead of competitors before they figure out GEO exists' },
  { icon: 'ri-emotion-sad-line', text: 'You\'re frustrated that good reviews and a good website aren\'t translating to enough enquiries' },
  { icon: 'ri-bar-chart-line', text: 'You already do SEO and want to add the next layer — extending your visibility into the AI search results your customers are actually using' },
  { icon: 'ri-trophy-line', text: 'You understand that first-mover advantage in GEO is real, and you want to move before your competitors do' },
];

const notFor = [
  'Businesses happy to stay invisible to AI-first customers',
  'Those expecting overnight results without building a quality foundation',
  'Businesses unwilling to invest in reviews, content, and consistent authority signals',
  'Anyone looking for a one-off fix rather than an ongoing competitive advantage',
];

const industries = [
  { icon: 'ri-hammer-line', name: 'Trades & Construction' },
  { icon: 'ri-heart-pulse-line', name: 'Health & Wellness' },
  { icon: 'ri-scales-3-line', name: 'Legal & Professional Services' },
  { icon: 'ri-restaurant-line', name: 'Hospitality & Dining' },
  { icon: 'ri-home-4-line', name: 'Real Estate' },
  { icon: 'ri-car-line', name: 'Auto & Mechanical' },
  { icon: 'ri-scissors-line', name: 'Beauty & Personal Care' },
  { icon: 'ri-graduation-cap-line', name: 'Education & Coaching' },
];

export default function WhoItsForSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="who-its-for" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-border to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-[600px] h-[500px] bg-ds-red/4 blur-[140px] rounded-full pointer-events-none -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Is This Right for You?</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              GEO IS FOR LOCAL
              <br />
              BUSINESSES WHO WANT
              <br />
              <span className="text-ds-red neon-text-red">TO OWN THE FUTURE.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              GEO / AEO is not right for every business at every stage. Here&apos;s who gets the most from it — and who should wait.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Perfect For grid */}
        <div className="mb-4 reveal">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-ds-red text-lg" />
            </div>
            <span className="font-display text-white text-sm tracking-widest uppercase">It&apos;s Perfect For You If:</span>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-ds-border/30">
            {perfectFor.map((item) => (
              <div key={item.text} className="bg-ds-bg p-6 flex items-start gap-4 group hover:bg-ds-bg-2 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center border border-ds-border group-hover:border-ds-red/40 transition-colors duration-300 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-white/40 group-hover:text-ds-red text-sm transition-colors duration-300`} />
                </div>
                <p className="font-body text-white/65 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Industries */}
        <div className="mb-10 reveal">
          <p className="font-display text-white/30 text-xs tracking-widest uppercase mb-4">Industries Seeing the Strongest GEO Results</p>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-px bg-ds-border/30">
            {industries.map((ind) => (
              <div key={ind.name} className="bg-ds-bg p-4 flex flex-col items-center gap-2 text-center group hover:bg-ds-bg-2 transition-colors duration-300">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${ind.icon} text-white/30 group-hover:text-ds-red text-base transition-colors duration-300`} />
                </div>
                <span className="font-body text-white/35 text-xs leading-tight group-hover:text-white/60 transition-colors duration-300 text-center">{ind.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 border border-ds-red/20 bg-ds-red/5 p-4 text-center">
            <p className="font-body text-white/60 text-sm">
              If a customer would ask AI about your industry, GEO is for you.{' '}
              <strong className="text-white">And in 2026, they&apos;re asking AI about everything.</strong>
            </p>
          </div>
        </div>

        {/* Not for */}
        <div className="border border-ds-border bg-ds-bg-2 p-8 reveal">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-circle-line text-white/30 text-lg" />
            </div>
            <span className="font-display text-white/40 text-xs tracking-widest uppercase">GEO Isn&apos;t Right For</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {notFor.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-close-line text-white/20 text-sm" />
                </div>
                <p className="font-body text-white/30 text-sm leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
